import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
/**
 * LiveActivityNotifier
 * --------------------
 * Premium, cinematic micro-notifications shown at the bottom-left of the
 * viewport. Pure ambient social proof — no fake urgency, no ecommerce
 * aggression, no timers.
 *
 * Rules:
 *  - Only renders on Home (/) and Sign pages (/:signId).
 *  - Hidden on /portal-de-usuario, /terminos, /privacidad, /reembolsos.
 *  - First notification appears 18–30s after mount.
 *  - Following notifications: every 35–70s (randomized).
 *  - Each notification is visible 4–6s.
 *  - Names rotate without immediate repeat.
 */
const GOLD = '#C9A84C';
const GOLD_LIGHT = '#E8D48B';
interface Person {
  name: string;
  city: string;
}
const PEOPLE: Person[] = [{
  name: 'Sofía',
  city: 'Miami'
}, {
  name: 'Alejandro',
  city: 'Houston'
}, {
  name: 'Camila',
  city: 'Orlando'
}, {
  name: 'Valentina',
  city: 'New Jersey'
}, {
  name: 'Daniel',
  city: 'Chicago'
}, {
  name: 'Isabella',
  city: 'Los Angeles'
}, {
  name: 'Mateo',
  city: 'Tampa'
}, {
  name: 'Valeria',
  city: 'Phoenix'
}, {
  name: 'Sebastián',
  city: 'Dallas'
}, {
  name: 'Gabriela',
  city: 'New York'
}, {
  name: 'Lucía',
  city: 'San Diego'
}, {
  name: 'Andrés',
  city: 'Atlanta'
}, {
  name: 'Renata',
  city: 'Las Vegas'
}, {
  name: 'Tomás',
  city: 'San Antonio'
}, {
  name: 'Emilia',
  city: 'Charlotte'
}];
const ACTIONS: string[] = ['desbloqueó su Reino.', 'accedió a la experiencia completa.', 'activó la Cámara Astral.', 'desbloqueó el Archivo Sombrío.', 'comenzó su experiencia inmersiva.', 'accedió al Reino completo.', 'desbloqueó nuevas capas de su Reino.', 'entró al Núcleo del Arquetipo.', 'activó su Ritual del Eclipse.', 'abrió el Códice de Compatibilidad.', 'ingresó a su Portal del Reino.'];
// Allowed prefixes — Home (/) and Sign pages (/:signId). Sign IDs are
// the only known top-level slugs aside from the explicit excluded list.
const EXCLUDED_PREFIXES = ['/portal-de-usuario', '/terminos', '/privacidad', '/reembolsos'];
function shouldShowOnPath(pathname: string): boolean {
  if (EXCLUDED_PREFIXES.some(p => pathname.startsWith(p))) return false;
  return true;
}
function rand(min: number, max: number): number {
  return Math.random() * (max - min) + min;
}
function pickAction(): string {
  return ACTIONS[Math.floor(Math.random() * ACTIONS.length)];
}
export function LiveActivityNotifier() {
  const location = useLocation();
  const [current, setCurrent] = useState<{
    person: Person;
    action: string;
    key: number;
  } | null>(null);
  // Track which people we've shown recently so we don't repeat immediately.
  const recentRef = useRef<string[]>([]);
  const timeoutsRef = useRef<number[]>([]);
  const mountedRef = useRef(true);
  const allowed = shouldShowOnPath(location.pathname);
  const pickPerson = (): Person => {
    const recent = recentRef.current;
    const pool = PEOPLE.filter(p => !recent.includes(p.name));
    const source = pool.length > 0 ? pool : PEOPLE;
    const choice = source[Math.floor(Math.random() * source.length)];
    recentRef.current = [...recent, choice.name].slice(-6); // keep last 6
    return choice;
  };
  const clearAllTimeouts = () => {
    timeoutsRef.current.forEach(id => window.clearTimeout(id));
    timeoutsRef.current = [];
  };
  const showNotification = () => {
    if (!mountedRef.current) return;
    const person = pickPerson();
    const action = pickAction();
    const visibleMs = rand(4000, 6000); // 4–6 s
    setCurrent({
      person,
      action,
      key: Date.now()
    });
    const hideId = window.setTimeout(() => {
      if (!mountedRef.current) return;
      setCurrent(null);
      // Schedule the next one between 35–70 s after dismiss
      const nextGapMs = rand(35000, 70000);
      const nextId = window.setTimeout(showNotification, nextGapMs);
      timeoutsRef.current.push(nextId);
    }, visibleMs);
    timeoutsRef.current.push(hideId);
  };
  useEffect(() => {
    mountedRef.current = true;
    if (!allowed) {
      // On excluded routes — clear pending notifications and current.
      clearAllTimeouts();
      setCurrent(null);
      return () => {
        mountedRef.current = false;
      };
    }
    // First appearance: 18–30 s after entering an allowed route.
    const initialDelay = rand(18000, 30000);
    const firstId = window.setTimeout(showNotification, initialDelay);
    timeoutsRef.current.push(firstId);
    return () => {
      mountedRef.current = false;
      clearAllTimeouts();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed]);
  if (!allowed) return null;
  return <div aria-live="polite" aria-atomic="true" className="fixed bottom-4 left-4 sm:bottom-6 sm:left-6 z-[110] pointer-events-none" style={{
    maxWidth: 'calc(100vw - 32px)'
  }} data-id="element-698">
      <AnimatePresence mode="wait" data-id="element-699">
        {current && <motion.div key={current.key} initial={{
        opacity: 0,
        y: 12,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 6,
        scale: 0.99
      }} transition={{
        duration: 0.6,
        ease: [0.16, 1, 0.3, 1]
      }} className="relative flex items-center gap-2.5 rounded-full pl-3 pr-4 py-2 sm:py-2.5 overflow-hidden" style={{
        background: 'linear-gradient(135deg, rgba(7,7,11,0.92) 0%, rgba(11,11,18,0.92) 100%)',
        border: `1px solid ${GOLD}30`,
        boxShadow: `0 10px 30px rgba(0,0,0,0.55), 0 0 24px ${GOLD}25`,
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
        maxWidth: 340
      }} data-id="element-700">
            {/* Subtle inner gold haze */}
            <div className="absolute inset-0 pointer-events-none rounded-full" style={{
          background: `radial-gradient(ellipse at left center, ${GOLD}14 0%, transparent 70%)`
        }} data-id="element-701" />

            {/* Pulsing gold dot */}
            <div className="relative flex-shrink-0 w-[10px] h-[10px]" data-id="element-702">
              <motion.div className="absolute inset-0 rounded-full" style={{
            backgroundColor: GOLD,
            boxShadow: `0 0 10px ${GOLD}, 0 0 18px ${GOLD_LIGHT}80`
          }} animate={{
            scale: [1, 1.25, 1],
            opacity: [0.85, 1, 0.85]
          }} transition={{
            duration: 1.8,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-703" />
            </div>

            {/* Message */}
            <p className="relative text-[11.5px] sm:text-[12.5px] font-serif leading-tight tracking-[0.01em] whitespace-nowrap overflow-hidden text-ellipsis" style={{
          color: '#F5F0E1',
          textShadow: '0 1px 6px rgba(0,0,0,0.8)'
        }} data-id="element-704">
              <span className="font-semibold" style={{
            color: GOLD_LIGHT
          }} data-id="element-705">
                {current.person.name}
              </span>
              <span className="opacity-60 mx-1.5" aria-hidden="true" data-id="element-706">
                ·
              </span>
              <span className="opacity-80" style={{
            color: 'rgba(232,212,139,0.85)'
          }} data-id="element-707">
                {current.person.city}
              </span>
              <span className="opacity-50 mx-1.5" aria-hidden="true" data-id="element-708">
                —
              </span>
              <span style={{
            color: 'rgba(245,240,225,0.85)'
          }} data-id="element-709">
                {current.action}
              </span>
            </p>
          </motion.div>}
      </AnimatePresence>
    </div>;
}